import { useState } from 'react';
import { Video, Settings, Info, ChevronRight } from 'lucide-react';
import { TaskSubmit } from './components/TaskSubmit';
import { TaskList } from './components/TaskList';
import { useTaskStore } from './store/taskStore';

function App() {
  const [activeTab, setActiveTab] = useState<'submit' | 'list'>('submit');
  const { tasks } = useTaskStore();
  const pendingTasksCount = tasks.filter(
    (t) => t.status === 'in_progress' || t.status === 'pending'
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* 头部导航 */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <Video className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  Video Localizer
                </h1>
                <p className="text-xs text-gray-500">
                  Douyin to TikTok (UAE)
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {pendingTasksCount > 0 && (
                <div className="relative">
                  <div className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  <span className="text-sm font-medium text-blue-600">
                    {pendingTasksCount} {pendingTasksCount === 1 ? 'task' : 'tasks'} in progress
                  </span>
                </div>
              )}
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <Info className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 主要内容区域 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 选项卡导航 */}
        <div className="flex space-x-1 bg-white rounded-lg shadow-sm p-1 mb-6">
          <button
            onClick={() => setActiveTab('submit')}
            className={`
              flex-1 flex items-center justify-center px-4 py-3 rounded-md text-sm font-medium transition-all
              ${activeTab === 'submit'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            Submit Task
          </button>
          <button
            onClick={() => setActiveTab('list')}
            className={`
              flex-1 flex items-center justify-center px-4 py-3 rounded-md text-sm font-medium transition-all
              ${activeTab === 'list'
                ? 'bg-blue-600 text-white shadow-sm'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }
            `}
          >
            <span className="flex items-center">
              Task List
              {tasks.length > 0 && (
                <span className="ml-2 bg-blue-200 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                  {tasks.length}
                </span>
              )}
            </span>
          </button>
        </div>

        {/* 内容区域 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 左侧面板 */}
          <div className="lg:col-span-2">
            {activeTab === 'submit' && <TaskSubmit />}
            {activeTab === 'list' && <TaskList />}
          </div>

          {/* 右侧信息面板 */}
          <div className="lg:col-span-1 space-y-6">
            {/* 系统状态 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API Status</span>
                  <span className="text-sm font-medium text-green-600">Online</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Queue</span>
                  <span className="text-sm font-medium text-blue-600">
                    {pendingTasksCount} pending
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Completed Today</span>
                  <span className="text-sm font-medium text-gray-900">
                    {tasks.filter(t => t.status === 'completed').length}
                  </span>
                </div>
              </div>
            </div>

            {/* 处理流程说明 */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Flow</h3>
              <div className="space-y-3">
                {[
                  'Fetch source video',
                  'Extract & recognize speech',
                  'Translate to Arabic',
                  'Synthesize Arabic voice',
                  'Merge video & audio',
                  'Upload to TikTok',
                ].map((step, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-xs font-medium">
                      {index + 1}
                    </div>
                    <p className="text-sm text-gray-700">{step}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* 快速提示 */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg shadow-md p-6 text-white">
              <h3 className="text-lg font-semibold mb-2">Quick Tips</h3>
              <ul className="space-y-2 text-sm text-blue-50">
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Supported video formats: MP4, WebM, MOV</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Maximum file size: 500MB</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Processing time: ~5-15 minutes</span>
                </li>
                <li className="flex items-start">
                  <ChevronRight className="w-4 h-4 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Target language: Arabic (UAE)</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-sm text-gray-500">
            © 2025 Video Localizer. Automated video localization for TikTok UAE.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;